// src/pages/SubscriberChatPage.jsx
// Route: /subscriber/chat
//
// Feature #2 — subscriber chat with their care team. Mirrors
// PatientCareTeamPage.jsx almost exactly: chatMessagingAPI, useHumanChat,
// HumanChatWindow, and NewConversationPicker are all already
// role-agnostic (chatMessagingAPI just reads the shared previu_token;
// useHumanChat/HumanChatWindow operate purely on conversationId/senderId,
// never on role) — reused completely unmodified here. Only one difference
// from the patient version: a subscriber has a single relationship type
// (SUBSCRIBER_NAVIGATOR — no doctor relationship of their own), so there's
// one "+ Message" button, not two.
//
// No Figma frame exists for this screen — same minimal styling as every
// other Guardian Dubai page built so far.

import { useState, useEffect, useCallback, useRef } from 'react';
import { SubscriberAppShell } from '../components/SubscriberLayout';
import { useSubscriber } from '../context/SubscriberContext';
import { useToast } from '../context/ToastContext';
import { chatMessagingAPI } from '../services/chatMessagingAPI';
import { useHumanChat } from '../hooks/useHumanChat';
import HumanChatWindow from '../components/chat/HumanChatWindow';
import NewConversationPicker from '../components/chat/NewConversationPicker';
import avatarTint, { avatarAccentFill } from '../utils/avatarTint';
import Icon from '../components/Icon';

function initials(name) {
  if (!name) return '?';
  return name.trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase();
}

export default function SubscriberChatPage() {
  const { subscriber } = useSubscriber();
  const { toast } = useToast();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const loadConversations = useCallback(() => {
    setLoading(true);
    chatMessagingAPI.list()
      .then(setConversations)
      .catch((e) => setError(e.message || 'Could not load your messages.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  // Same auto-select-most-recent-once pattern as PatientCareTeamPage.jsx.
  const autoSelectedRef = useRef(false);
  useEffect(() => {
    if (autoSelectedRef.current) return;
    if (activeId || conversations.length === 0) return;
    const mostRecent = conversations.reduce((latest, c) => {
      const cTime = new Date(c.lastMessageAt || c.createdAt).getTime();
      const latestTime = new Date(latest.lastMessageAt || latest.createdAt).getTime();
      return cTime > latestTime ? c : latest;
    });
    setActiveId(mostRecent.id);
    autoSelectedRef.current = true;
  }, [conversations, activeId]);

  const active = conversations.find(c => c.id === activeId) || null;
  const chat = useHumanChat(activeId);

  async function handlePickCounterpart(counterpart) {
    try {
      const conversation = await chatMessagingAPI.createOrGet('SUBSCRIBER_NAVIGATOR', counterpart.id);
      setPickerOpen(false);
      loadConversations();
      setActiveId(conversation.id);
    } catch (e) {
      toast.error(e.message || 'Could not start that conversation.');
    }
  }

  const noActivePlan = subscriber?.subscriptionStatus !== 'ACTIVE';

  return (
    <SubscriberAppShell breadcrumbs={[{ label: 'Chat with Navigator' }]}>
      {noActivePlan && (
        <div className="alert" style={{
          maxWidth: 900, margin: '0 auto 16px', background: 'var(--amber-bg)', color: 'var(--amber)',
        }}>
          Chat with your Navigator opens once your plan is active.
        </div>
      )}

      {/* Same horizontal avatar strip as PatientCareTeamPage.jsx, replacing
          the old fixed 280px vertical list + mobile "← Back" swap. No
          filter row — a subscriber only ever messages navigators, so an
          All/Navigators tab pair would be redundant (see
          TeamInboxPage.jsx's showFilter for the same reasoning). */}
      <div className="chat-pane" style={{
        flexDirection: 'column', gap: 14,
        opacity: noActivePlan ? 0.5 : 1,
        pointerEvents: noActivePlan ? 'none' : 'auto',
      }}>
        <div className="panel" style={{ flexShrink: 0, padding: '14px 16px' }}>
          <div className="panel-head" style={{ marginBottom: 16, flexWrap: 'nowrap' }}>
            <span style={{
              fontSize: 11, fontWeight: 700, color: 'var(--text-muted)',
              textTransform: 'uppercase', letterSpacing: '.08em', flexShrink: 0,
            }}>
              Care Team
            </span>
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              title="Message a navigator"
              aria-label="Message a navigator"
              className="icon-btn-round"
            >
              <Icon name="chat" size={14} />
            </button>
          </div>

          {loading && (
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading…</div>
          )}
          {error && (
            <div style={{ fontSize: 13, color: 'var(--red)' }}>{error}</div>
          )}
          {!loading && !error && conversations.length === 0 && (
            <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.6 }}>
              No conversations yet. Tap the message icon to reach your Navigator.
            </div>
          )}

          <div style={{
            display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 2,
            scrollSnapType: 'x mandatory',
          }}>
            {conversations.map((c) => {
              const isActive = c.id === activeId;
              const accent = avatarAccentFill(c.counterpartName);
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setActiveId(c.id)}
                  title={`${c.counterpartName} — Navigator`}
                  style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                    padding: '20px 10px 16px', cursor: 'pointer', minWidth: 0,
                    flex: '0 0 calc((100% - 24px) / 3)', scrollSnapAlign: 'start',
                    background: 'var(--bg-subtle)',
                    border: isActive ? `2px solid ${accent}` : '1px solid var(--border)',
                    borderRadius: 'var(--radius-lg)',
                  }}
                >
                  <div style={{ position: 'relative' }}>
                    <div
                      className={`avatar ${avatarTint(c.counterpartName)}`}
                      aria-hidden="true"
                      style={{
                        width: 56, height: 56, fontSize: 18,
                        boxShadow: isActive ? `0 0 0 2px var(--bg-subtle), 0 0 0 4px ${accent}` : 'none',
                      }}
                    >
                      {initials(c.counterpartName)}
                    </div>
                    {c.unreadCount > 0 && (
                      <span style={{
                        position: 'absolute', top: -2, right: -2, minWidth: 19, height: 19,
                        borderRadius: 999, background: 'var(--red)', color: 'var(--text-on-brand-dark)',
                        fontSize: 10.5, fontWeight: 700, display: 'flex', alignItems: 'center',
                        justifyContent: 'center', padding: '0 4px', border: '2px solid var(--bg-subtle)',
                      }}>
                        {c.unreadCount}
                      </span>
                    )}
                  </div>
                  <div style={{
                    fontSize: 14, fontWeight: 700, color: 'var(--text-primary)',
                    textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap', width: '100%',
                  }}>
                    {c.counterpartName.trim().split(/\s+/)[0]}
                  </div>
                  <div style={{
                    fontSize: 11.5, fontWeight: 600,
                    color: isActive ? accent : 'var(--text-muted)',
                  }}>
                    Navigator
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
          {active ? (
            <HumanChatWindow
              counterpartName={active.counterpartName}
              counterpartRole={active.counterpartRole}
              counterpartId={active.counterpartId}
              messages={chat.messages}
              loading={chat.loading}
              error={chat.error}
              sending={chat.sending}
              onSend={chat.sendMessage}
            />
          ) : (
            <div className="panel" style={{
              height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'var(--text-muted)', fontSize: 13,
            }}>
              Select a conversation to view messages.
            </div>
          )}
        </div>
      </div>

      {pickerOpen && (
        <NewConversationPicker
          conversationType="SUBSCRIBER_NAVIGATOR"
          title="Message a navigator"
          onSelect={handlePickCounterpart}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </SubscriberAppShell>
  );
}
